import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MenuItem, MenuList } from "@mui/material";
import { isSegFeatureEnabled, SegFlags, translate } from "../../../../../../../../common/providers";
import {
	CustomDialog,
	CustomIconButton,
	CustomTooltip,
	DropDown,
	icon,
	IconComponent,
} from "../../../../../../../../common/components";
import styles from "./LockHandler.module.css";
import { useApi, useTrackEvent } from "../../../../../../../../common/hooks";
import { DocumentService, SmartViewService, ValidatorService } from "../../../../../../../../api";
import { updateCaptureFreeze } from "../../../../../../slice/document/document-slice";
import { ROLES } from "../../../../../../constants/constants";
import { checkReqValMode } from "../../../../../../utils/utils";

const LockHandler = () => {
	const lockMenuRef = useRef();
	const dispatch = useDispatch();
	const trackEvent = useTrackEvent();
	const [open, setOpen] = useState(false);
	const [unfreezeDialogOpen, setUnfreezeDialogOpen] = useState(false);
	const [validatedReqsCount, setValidatedReqsCount] = useState(0);
	const [lockedReqsCount, setLockedReqsCount] = useState(0);
	const documentId = useSelector(({ srDocument }) => srDocument.documentId);
	const captureLocked = useSelector(({ srDocument }) => srDocument.isFrozen);
	const mode = useSelector(({ srDocument }) => srDocument.mode);
	const roles = useSelector(({ srDocument }) => srDocument.roles);
	const { call: freezeDocument } = useApi(DocumentService.freezeDocument);
	const { call: freezeAllValidated } = useApi(ValidatorService.freezeAllValidated);
	const { call: unfreezeAllValidated } = useApi(ValidatorService.unfreezeAllValidated);
	const { call: getValidatedReqsCount } = useApi(SmartViewService.getValidatedReqsCount);
	const { call: getLockedUnlockedReqsCount } = useApi(SmartViewService.getLockedUnlockedReqsCount);

	useEffect(() => {
		if (!open) {
			return;
		}
		getValidatedReqsCount({ documentId })
			.then((res) => setValidatedReqsCount(res.count))
			.catch((err) => console.error("Validated count error:", err));

		getLockedUnlockedReqsCount({ documentId })
			.then((res) => setLockedReqsCount(res.frozen))
			.catch((err) => console.error("Locked count error:", err));
	}, [open, documentId, getLockedUnlockedReqsCount, getValidatedReqsCount]);

	const handleDropdown = () => {
		setOpen((prev) => !prev);
	};
	const handleLockCapture = () => {
		freezeDocument({ docId: documentId })
			.then(() => {
				dispatch(updateCaptureFreeze());
				if (captureLocked) {
					trackEvent("MyReview_Validator_Capture_Unlock");
				} else {
					trackEvent("MyReview_Validator_Capture_Lock");
				}
			})
			.catch((err) => console.error(err));
	};
	const handleFreezeValidated = () => {
		freezeAllValidated({ docId: documentId })
			.then(() => {
				trackEvent("MyReview_Validator_Req_Lock_All_Validated_Reqs");
				window.location.reload();
			})
			.catch((err) => console.error(err));
	};
	const handleUnfreezeValidated = () => {
		unfreezeAllValidated({ docId: documentId })
			.then(() => {
				trackEvent("MyReview_Validator_Req_Unock_All_Validated_Reqs");
				setUnfreezeDialogOpen(true);
			})
			.catch((err) => console.error(err));
	};
	const handleCloseUnfreezeDialog = () => {
		window.location.reload();
	};
	const hasValidatorRole = Array.isArray(roles) && roles.some((x) => x === ROLES.VALIDATOR.name.toUpperCase());
	const lockButton = (
		<span>
			<CustomIconButton
				ref={lockMenuRef}
				disabled={mode !== ROLES.VALIDATOR.mode || !hasValidatorRole}
				icon={captureLocked ? icon.faLock : icon.faLockOpen}
				size="sm"
				variant="outlined"
				onClick={handleDropdown}
			/>
		</span>
	);
	const tooltipTitle = captureLocked
		? translate("smart-review.header.freeze-capture.unlocked")
		: translate("smart-review.header.freeze-capture.locked");
	return (
		<div>
			{isSegFeatureEnabled(SegFlags.RIGHTS_ON_DOCUMENTS) && (
				<>
					{open ? (
						lockButton
					) : (
						<CustomTooltip className={styles.tooltip} disableHoverListener={open} title={tooltipTitle}>
							{lockButton}
						</CustomTooltip>
					)}
					{mode === ROLES.VALIDATOR.mode && hasValidatorRole && checkReqValMode(mode) && (
						<DropDown ref={lockMenuRef} open={open} onClose={handleDropdown}>
							<MenuList className={styles.menuList}>
								<MenuItem className={styles.menuItem} onClick={handleLockCapture}>
									<IconComponent
										className={styles.menuItem__icon}
										color="var(--grey-600)"
										icon={captureLocked ? icon.faLockOpen : icon.faLock}
										size="xs"
									/>
									<span className={styles.menuItem__text}>
										{captureLocked
											? translate("smart-review.header.capture.unlocked")
											: translate("smart-review.header.capture.locked")}
									</span>
								</MenuItem>
								<MenuItem
									className={styles.menuItem}
									disabled={validatedReqsCount === 0}
									onClick={handleFreezeValidated}
								>
									<IconComponent
										className={styles.menuItem__icon}
										color="var(--grey-600)"
										icon={icon.faLock}
										size="xs"
									/>
									<span className={styles.menuItem__text}>
										{translate("smart-review.header.capture.validated-requirement-locked", {
											nbReqs: validatedReqsCount,
										})}
									</span>
								</MenuItem>
								<MenuItem
									className={styles.menuItem}
									disabled={lockedReqsCount === 0}
									onClick={handleUnfreezeValidated}
								>
									<IconComponent
										className={styles.menuItem__icon}
										color="var(--grey-600)"
										icon={icon.faLockOpen}
										size="xs"
									/>
									<span className={styles.menuItem__text}>
										{translate("smart-review.header.capture.requirement-unlocked", {
											nbReqs: lockedReqsCount,
										})}
									</span>
								</MenuItem>
							</MenuList>
						</DropDown>
					)}

					<CustomDialog
						closeLabel={translate("common:btn.close")}
						maxWidth="xs"
						open={unfreezeDialogOpen}
						subTitle={translate("smart-review.list-pdf-images.unfreeze-dialog.content")}
						title={translate("smart-review.list-pdf-images.unfreeze-dialog.title")}
						onClose={handleCloseUnfreezeDialog}
					/>
				</>
			)}
		</div>
	);
};

export default LockHandler;
